import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import {
  Building2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  FileText,
  Users,
  CreditCard,
  Calendar,
  Activity,
  BarChart3,
  MapPin,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Target,
  PiggyBank,
  Calculator,
  Heart,
  Ambulance,
  Share2,
  Copy,
  ExternalLink,
} from "lucide-react";
import type { HospitalPartner, ProviderPayment, HospitalPartnerLocation, RevenueCycleSnapshot, RoiAssessment, EmergencyCareRequest } from "@shared/schema";
import { PageAbout } from "@/components/page-about";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";

interface PartnerDashboardData {
  partner: HospitalPartner;
  paymentRequests: ProviderPayment[];
  stats: {
    totalOrders: number;
    completedOrders: number;
    pendingPayments: number;
    totalRevenue: number;
  };
}

interface RevenueCycleDashboardData {
  partner: HospitalPartner;
  locations: HospitalPartnerLocation[];
  currentMetrics: RevenueCycleSnapshot | null;
  previousMetrics: RevenueCycleSnapshot | null;
  latestRoiAssessment: RoiAssessment | null;
  trendData: RevenueCycleSnapshot[];
}

const paymentRequestSchema = z.object({
  walletId: z.string().min(1, "Wallet is required"),
  amount: z.number().min(100, "Minimum amount is $1"),
  serviceDate: z.string().optional(),
  serviceDescription: z.string().min(1, "Service description is required"),
  invoiceNumber: z.string().optional(),
});

type PaymentRequestFormData = z.infer<typeof paymentRequestSchema>;

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof CheckCircle }> = {
    pending: { variant: "secondary", icon: Clock },
    approved: { variant: "default", icon: CheckCircle },
    rejected: { variant: "destructive", icon: XCircle },
    processing: { variant: "secondary", icon: Activity },
    completed: { variant: "default", icon: CheckCircle },
    failed: { variant: "destructive", icon: XCircle },
  };
  
  const config = variants[status] || { variant: "secondary", icon: AlertCircle };
  const Icon = config.icon;
  
  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {status}
    </Badge>
  );
}

function MetricCard({ title, value, icon: Icon, description }: { title: string; value: string | number; icon: typeof DollarSign; description?: string }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );
}

function PaymentRequestDialog({ partnerId, onSuccess }: { partnerId: string; onSuccess: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  
  const { data: wallets } = useQuery<{ id: string; patientName: string; balance: number }[]>({
    queryKey: ["/api/partner-portal", partnerId, "linked-wallets"],
    enabled: open,
  });
  
  const form = useForm<PaymentRequestFormData>({
    mode: 'onBlur',
    resolver: zodResolver(paymentRequestSchema),
    defaultValues: {
      walletId: "",
      amount: 0,
      serviceDescription: "",
      invoiceNumber: "",
    },
  });
  
  const createPaymentMutation = useMutation({
    mutationFn: (data: PaymentRequestFormData) => 
      apiRequest("POST", `/api/partner-portal/${partnerId}/payment-requests`, data),
    onSuccess: () => {
      toast({ title: t("partnerPortal.paymentRequestCreated", "Payment request submitted") });
      setOpen(false);
      form.reset();
      onSuccess();
    },
    onError: (error: any) => {
      toast({ 
        title: t("common.error", "Error"), 
        description: error.message, 
        variant: "destructive" 
      });
    },
  });
  
  const onSubmit = (data: PaymentRequestFormData) => {
    createPaymentMutation.mutate(data);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="button-new-payment-request">
          <Plus className="h-4 w-4 mr-2" />
          {t("partnerPortal.newPaymentRequest", "New Payment Request")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("partnerPortal.requestPayment", "Request Payment from Patient Wallet")}</DialogTitle>
          <DialogDescription>
            {t("partnerPortal.requestPaymentDesc", "Submit a payment request for services rendered. The patient will need to approve this request.")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="walletId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("partnerPortal.selectWallet", "Patient Wallet")}</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="w-full h-10 px-3 border rounded-md bg-background"
                      data-testid="select-wallet"
                    >
                      <option value="">{t("partnerPortal.selectPatient", "Select a patient...")}</option>
                      {wallets?.map(wallet => (
                        <option key={wallet.id} value={wallet.id}>
                          {wallet.patientName} (${(wallet.balance / 100).toLocaleString()} available)
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("partnerPortal.amount", "Amount")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="pl-7"
                        {...field}
                        onChange={(e) => field.onChange(Math.round(parseFloat(e.target.value || "0") * 100))}
                        value={field.value ? (field.value / 100).toFixed(2) : ""}
                        data-testid="input-amount"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="serviceDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("partnerPortal.serviceDescription", "Service Description")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("partnerPortal.serviceDescPlaceholder", "Describe the service provided...")}
                      {...field}
                      data-testid="input-service-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="invoiceNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("partnerPortal.invoiceNumber", "Invoice Number")} ({t("common.optional", "optional")})</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="INV-12345"
                      {...field}
                      data-testid="input-invoice-number"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={createPaymentMutation.isPending} data-testid="button-submit-payment-request">
                {createPaymentMutation.isPending ? t("common.submitting", "Submitting...") : t("partnerPortal.submitRequest", "Submit Request")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "$0.00";
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(1)}%`;
}

function MetricChange({ current, previous, suffix = "", isInverse = false }: { current: number | null | undefined; previous: number | null | undefined; suffix?: string; isInverse?: boolean }) {
  const currentVal = current ?? 0;
  const previousVal = previous ?? 0;
  
  if (previousVal === 0) return null;
  
  const change = ((currentVal - previousVal) / previousVal) * 100;
  const isPositive = isInverse ? change < 0 : change > 0;
  
  return (
    <span className={`flex items-center gap-1 text-xs ${isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
      {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(change).toFixed(1)}%{suffix}
    </span>
  );
}

function LocationsTab({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation();
  
  const { data: locationsData, isLoading } = useQuery<HospitalPartnerLocation[]>({
    queryKey: ["/api/partner-portal", partnerId, "locations"],
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading locations...</div>
      </div>
    );
  }
  
  const locations = locationsData || [];
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          {t("partnerPortal.locations", "Nationwide Locations")}
        </CardTitle>
        <CardDescription>
          All {locations.length} locations across the Children's Hospital network
        </CardDescription>
      </CardHeader>
      <CardContent>
        {locations.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No locations found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("partnerPortal.location", "Location")}</TableHead>
                  <TableHead>{t("partnerPortal.code", "Code")}</TableHead>
                  <TableHead>{t("partnerPortal.city", "City")}</TableHead>
                  <TableHead>{t("partnerPortal.state", "State")}</TableHead>
                  <TableHead>{t("partnerPortal.region", "Region")}</TableHead>
                  <TableHead className="text-right">{t("partnerPortal.collectionRate", "Collection Rate")}</TableHead>
                  <TableHead className="text-right">{t("partnerPortal.badDebtRate", "Bad Debt Rate")}</TableHead>
                  <TableHead className="text-right">{t("partnerPortal.denialRate", "Denial Rate")}</TableHead>
                  <TableHead>{t("partnerPortal.status", "Status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map(location => (
                  <TableRow key={location.id} data-testid={`row-location-${location.id}`}>
                    <TableCell className="font-medium">{location.locationName}</TableCell>
                    <TableCell className="font-mono text-sm">{location.locationCode}</TableCell>
                    <TableCell>{location.city}</TableCell>
                    <TableCell>{location.state}</TableCell>
                    <TableCell>{location.region || "-"}</TableCell>
                    <TableCell className="text-right font-medium">{formatPercentage(location.baselineCollectionRate)}</TableCell>
                    <TableCell className="text-right">{formatPercentage(location.baselineBadDebtRate)}</TableCell>
                    <TableCell className="text-right">{formatPercentage(location.baselineDenialRate)}</TableCell>
                    <TableCell>
                      <Badge variant={location.isActive ? "default" : "secondary"}>
                        {location.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const emergencyRequestSchema = z.object({
  patientName: z.string().min(2, "Patient name is required"),
  patientEmail: z.string().email().optional().or(z.literal("")),
  patientPhone: z.string().optional().or(z.literal("")),
  serviceDescription: z.string().min(5, "Service description is required"),
  serviceDate: z.string().min(1, "Service date is required"),
  serviceType: z.string().min(1, "Service type is required"),
  diagnosisCode: z.string().optional().or(z.literal("")),
  totalCost: z.string().min(1, "Total cost is required"),
  urgencyLevel: z.string().default("urgent"),
  hospitalNotes: z.string().optional().or(z.literal("")),
  consentToShare: z.boolean().default(false),
});

function EmergencyCareTab({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { data: emergencyRequests = [], isLoading } = useQuery<EmergencyCareRequest[]>({
    queryKey: ["/api/partner-portal", partnerId, "emergency-requests"],
  });

  const form = useForm<z.infer<typeof emergencyRequestSchema>>({
    mode: 'onBlur',
    resolver: zodResolver(emergencyRequestSchema),
    defaultValues: {
      patientName: "",
      patientEmail: "",
      patientPhone: "",
      serviceDescription: "",
      serviceDate: "",
      serviceType: "emergency_room",
      diagnosisCode: "",
      totalCost: "",
      urgencyLevel: "urgent",
      hospitalNotes: "",
      consentToShare: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: z.infer<typeof emergencyRequestSchema>) => {
      const costInCents = Math.round(parseFloat(values.totalCost) * 100);
      return apiRequest("POST", `/api/partner-portal/${partnerId}/emergency-requests`, {
        ...values,
        totalCost: costInCents,
      });
    },
    onSuccess: () => {
      toast({ title: t("partnerPortal.emergencyRequestCreated", "Emergency care request created successfully") });
      queryClient.invalidateQueries({ queryKey: ["/api/partner-portal", partnerId, "emergency-requests"] });
      form.reset();
      setCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const convertToPlanMutation = useMutation({
    mutationFn: async ({ requestId, months }: { requestId: string; months: number }) => {
      return apiRequest("POST", `/api/partner-portal/${partnerId}/emergency-requests/${requestId}/convert-to-plan`, { months });
    },
    onSuccess: () => {
      toast({ title: t("partnerPortal.paymentPlanCreated", "Payment schedule created successfully") });
      queryClient.invalidateQueries({ queryKey: ["/api/partner-portal", partnerId, "emergency-requests"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const copyShareLink = (shareToken: string) => {
    const url = `${window.location.origin}/emergency/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(shareToken);
    toast({ title: t("partnerPortal.linkCopied", "Share link copied to clipboard") });
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "default";
      case "fully_funded": return "default";
      case "partially_funded": return "secondary";
      case "payment_plan_created": return "outline";
      case "closed": return "secondary";
      default: return "outline";
    }
  };

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case "critical": return "destructive";
      case "urgent": return "default";
      case "standard": return "secondary";
      default: return "outline";
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ambulance className="h-5 w-5" />
              {t("partnerPortal.emergencyCareTitle", "Emergency Care Funding")}
            </CardTitle>
            <CardDescription>
              {t("partnerPortal.emergencyCareDesc", "Create funding requests for patients without traditional insurance who received urgent care. Generate shareable community support pages and convert remaining balances to flexible payment schedules.")}
            </CardDescription>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-create-emergency-request">
                <Plus className="h-4 w-4 mr-2" />
                {t("partnerPortal.newRequest", "New Request")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("partnerPortal.createEmergencyRequest", "Create Emergency Care Request")}</DialogTitle>
                <DialogDescription>
                  {t("partnerPortal.createEmergencyRequestDesc", "Enter patient and service details. A community support wallet will be created automatically.")}
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="patientName" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.patientName", "Patient Name")}</FormLabel>
                        <FormControl>
                          <Input placeholder="John D." {...field} data-testid="input-patient-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="totalCost" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.totalCost", "Total Cost ($)")}</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="2500.00" {...field} data-testid="input-total-cost" />
                        </FormControl>
                        <FormDescription>Enter amount in dollars (e.g. 8500 for $8,500)</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="patientEmail" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.patientEmail", "Patient Email (optional)")}</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="patient@email.com" {...field} data-testid="input-patient-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="patientPhone" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.patientPhone", "Patient Phone (optional)")}</FormLabel>
                        <FormControl>
                          <Input placeholder="(612) 555-0123" {...field} data-testid="input-patient-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="serviceDescription" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("partnerPortal.serviceDescription", "Service Description")}</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Emergency appendectomy, 2-day hospital stay..." {...field} data-testid="input-service-description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="serviceDate" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.serviceDate", "Service Date")}</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} data-testid="input-service-date" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="serviceType" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.serviceType", "Service Type")}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-service-type">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="emergency_room">Emergency Room</SelectItem>
                            <SelectItem value="urgent_care">Urgent Care</SelectItem>
                            <SelectItem value="surgery">Surgery</SelectItem>
                            <SelectItem value="icu">ICU / Critical Care</SelectItem>
                            <SelectItem value="trauma">Trauma</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="urgencyLevel" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.urgencyLevel", "Urgency Level")}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-urgency-level">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="diagnosisCode" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("partnerPortal.diagnosisCode", "Diagnosis Code (optional)")}</FormLabel>
                        <FormControl>
                          <Input placeholder="ICD-10 code" {...field} data-testid="input-diagnosis-code" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="hospitalNotes" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("partnerPortal.hospitalNotes", "Hospital Notes (optional)")}</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Internal notes about this case..." {...field} data-testid="input-hospital-notes" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="consentToShare" render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <input type="checkbox" checked={field.value} onChange={field.onChange} className="rounded" data-testid="checkbox-consent" />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        {t("partnerPortal.consentToShare", "Patient consented to sharing full name on community support page")}
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      {t("common.cancel", "Cancel")}
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-emergency-request">
                      {createMutation.isPending ? t("common.creating", "Creating...") : t("partnerPortal.createRequest", "Create Request")}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">{t("common.loading", "Loading...")}</div>
            </div>
          ) : emergencyRequests.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <Heart className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="text-muted-foreground">
                {t("partnerPortal.noEmergencyRequests", "No emergency care requests yet. Create your first request to generate a community support page for a patient without traditional insurance.")}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("partnerPortal.patient", "Patient")}</TableHead>
                  <TableHead>{t("partnerPortal.service", "Service")}</TableHead>
                  <TableHead>{t("partnerPortal.cost", "Cost")}</TableHead>
                  <TableHead>{t("partnerPortal.funded", "Funded")}</TableHead>
                  <TableHead>{t("partnerPortal.urgency", "Urgency")}</TableHead>
                  <TableHead>{t("partnerPortal.status", "Status")}</TableHead>
                  <TableHead>{t("partnerPortal.actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emergencyRequests.map(request => {
                  const fundingPercent = request.totalCost > 0 ? Math.round((request.amountFunded / request.totalCost) * 100) : 0;
                  const remaining = request.totalCost - request.amountFunded;

                  return (
                    <TableRow key={request.id} data-testid={`row-emergency-${request.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{request.patientName}</p>
                          <p className="text-sm text-muted-foreground">
                            {request.serviceDate ? format(new Date(request.serviceDate), "MMM d, yyyy") : "-"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        <p className="truncate">{request.serviceDescription}</p>
                        <Badge variant="outline" className="mt-1">{request.serviceType.replace(/_/g, " ")}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(request.totalCost)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{formatCurrency(request.amountFunded)}</p>
                          <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(fundingPercent, 100)}%` }} />
                          </div>
                          <p className="text-xs text-muted-foreground">{fundingPercent}%</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getUrgencyColor(request.urgencyLevel) as any}>
                          {request.urgencyLevel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(request.status) as any}>
                          {request.status.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => request.shareToken && copyShareLink(request.shareToken)}
                            data-testid={`button-copy-link-${request.id}`}
                          >
                            {copiedToken === request.shareToken ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => request.shareToken && window.open(`/emergency/${request.shareToken}`, "_blank")}
                            data-testid={`button-view-page-${request.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          {remaining > 0 && request.status !== "payment_plan_created" && request.status !== "closed" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => convertToPlanMutation.mutate({ requestId: request.id, months: 12 })}
                              disabled={convertToPlanMutation.isPending}
                              data-testid={`button-convert-plan-${request.id}`}
                            >
                              <Calculator className="h-3 w-3 mr-1" />
                              {t("partnerPortal.convertToPlan", "Payment Schedule")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RevenueCycleTab({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selectedLocation, setSelectedLocation] = useState<string>("all");
  
  const { data: revenueCycleData, isLoading } = useQuery<RevenueCycleDashboardData>({
    queryKey: ["/api/partner-portal", partnerId, "revenue-cycle", selectedLocation !== "all" ? selectedLocation : undefined],
  });
  
  const downloadReportMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", `/api/partner-portal/${partnerId}/roi-report${selectedLocation !== "all" ? `?locationId=${selectedLocation}` : ""}`);
      return response;
    },
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roi-report-${format(new Date(), "yyyy-MM-dd")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Report downloaded successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading revenue cycle data...</div>
      </div>
    );
  }
  
  if (!revenueCycleData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">No revenue cycle data available</div>
      </div>
    );
  }
  
  const { locations, currentMetrics, previousMetrics, latestRoiAssessment, trendData } = revenueCycleData;
  
  const chartData = trendData.map(snapshot => ({
    date: snapshot.snapshotDate ? format(new Date(snapshot.snapshotDate), "MMM d") : "",
    walletRevenue: (snapshot.totalWalletFundedRevenue || 0) / 100,
    collected: (snapshot.totalCollected || 0) / 100,
    patients: snapshot.patientsServed || 0,
  })).reverse();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Revenue Cycle Performance</h3>
          <p className="text-sm text-muted-foreground">
            {selectedLocation === "all" ? "National roll-up across all locations" : "Location-specific metrics"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-[220px]" data-testid="select-location">
              <MapPin className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(location => (
                <SelectItem key={location.id} value={location.id}>
                  {location.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => downloadReportMutation.mutate()}
            disabled={downloadReportMutation.isPending}
            data-testid="button-download-report"
          >
            <Download className="h-4 w-4 mr-2" />
            Download ROI Report
          </Button>
        </div>
      </div>

      <Tabs defaultValue="analytics" className="w-full">
        <TabsList className="flex-wrap">
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <Activity className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="monthly-stats" data-testid="tab-monthly-stats">
            <BarChart3 className="h-4 w-4 mr-2" />
            Monthly Stats
          </TabsTrigger>
          <TabsTrigger value="performance-charts" data-testid="tab-performance-charts">
            <TrendingUp className="h-4 w-4 mr-2" />
            Performance Charts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Wallet-Funded Revenue</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(currentMetrics?.totalWalletFundedRevenue)}</div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">vs previous period</span>
              <MetricChange 
                current={currentMetrics?.totalWalletFundedRevenue} 
                previous={previousMetrics?.totalWalletFundedRevenue} 
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercentage(currentMetrics?.collectionRate)}</div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">of billed amount</span>
              <MetricChange 
                current={currentMetrics?.collectionRate} 
                previous={previousMetrics?.collectionRate} 
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Denial Rate</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercentage(currentMetrics?.denialRate)}</div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">claims denied</span>
              <MetricChange 
                current={currentMetrics?.denialRate} 
                previous={previousMetrics?.denialRate} 
                isInverse={true}
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Patients with Wallets</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentMetrics?.patientsWithWallets || 0}</div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">active pre-funded</span>
              <MetricChange 
                current={currentMetrics?.patientsWithWallets} 
                previous={previousMetrics?.patientsWithWallets} 
              />
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Revenue Trend
            </CardTitle>
            <CardDescription>Wallet-funded collections over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs fill-muted-foreground" />
                  <YAxis className="text-xs fill-muted-foreground" tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                  <Tooltip 
                    formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="walletRevenue" 
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary) / 0.2)" 
                    name="Wallet Revenue"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              ROI Summary
            </CardTitle>
            <CardDescription>Return on investment from HealthWallet partnership</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {latestRoiAssessment ? (
              <>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-muted-foreground">Total Benefits</span>
                  <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(latestRoiAssessment.totalBenefits)}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Wallet Collections</span>
                    <span>{formatCurrency(latestRoiAssessment.walletFundedCollections)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Avoided Bad Debt</span>
                    <span>{formatCurrency(latestRoiAssessment.avoidedBadDebt)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Admin Savings</span>
                    <span>{formatCurrency(latestRoiAssessment.adminEfficiencySavings)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-t">
                  <span className="text-muted-foreground">Total Costs</span>
                  <span className="text-lg font-semibold">{formatCurrency(latestRoiAssessment.totalCosts)}</span>
                </div>
                <div className="flex items-center justify-between py-3 bg-muted/50 rounded-lg px-4">
                  <span className="font-medium">Net ROI</span>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${(latestRoiAssessment.roiPercentage || 0) >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                      {formatPercentage(latestRoiAssessment.roiPercentage)}
                    </div>
                    <div className="text-sm text-muted-foreground">{formatCurrency(latestRoiAssessment.netValue)} net value</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <Badge variant={latestRoiAssessment.status === "approved" ? "default" : "secondary"}>
                    {latestRoiAssessment.status}
                  </Badge>
                  <span>
                    Period: {latestRoiAssessment.periodStart ? format(new Date(latestRoiAssessment.periodStart), "MMM d") : ""} - {latestRoiAssessment.periodEnd ? format(new Date(latestRoiAssessment.periodEnd), "MMM d, yyyy") : ""}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <PiggyBank className="h-12 w-12 mb-4" />
                <p className="text-center">No ROI assessment available yet.<br />Data will appear after sufficient transaction history.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="monthly-stats" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Statistics</CardTitle>
              <CardDescription>Month-over-month performance metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" className="text-xs fill-muted-foreground" />
                    <YAxis className="text-xs fill-muted-foreground" tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toLocaleString()}`, ""]}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                    />
                    <Bar dataKey="walletRevenue" fill="hsl(var(--primary))" name="Wallet Revenue" />
                    <Bar dataKey="collected" fill="hsl(var(--secondary))" name="Collected" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance-charts" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Revenue Trend
                </CardTitle>
                <CardDescription>Wallet-funded collections over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" className="text-xs fill-muted-foreground" />
                      <YAxis className="text-xs fill-muted-foreground" tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                      <Tooltip 
                        formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="walletRevenue" 
                        stroke="hsl(var(--primary))" 
                        fill="hsl(var(--primary) / 0.2)" 
                        name="Wallet Revenue"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  ROI Summary
                </CardTitle>
                <CardDescription>Return on investment from HealthWallet partnership</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {latestRoiAssessment ? (
                  <>
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-muted-foreground">Total Benefits</span>
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(latestRoiAssessment.totalBenefits)}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Wallet Collections</span>
                        <span>{formatCurrency(latestRoiAssessment.walletFundedCollections)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Avoided Bad Debt</span>
                        <span>{formatCurrency(latestRoiAssessment.avoidedBadDebt)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Admin Savings</span>
                        <span>{formatCurrency(latestRoiAssessment.adminEfficiencySavings)}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-3 border-t">
                      <span className="text-muted-foreground">Total Costs</span>
                      <span className="text-lg font-semibold">{formatCurrency(latestRoiAssessment.totalCosts)}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 bg-muted/50 rounded-lg px-4">
                      <span className="font-medium">Net ROI</span>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${(latestRoiAssessment.roiPercentage || 0) >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                          {formatPercentage(latestRoiAssessment.roiPercentage)}
                        </div>
                        <div className="text-sm text-muted-foreground">{formatCurrency(latestRoiAssessment.netValue)} net value</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <PiggyBank className="h-12 w-12 mb-4" />
                    <p className="text-center">No ROI assessment available yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {locations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Locations
            </CardTitle>
            <CardDescription>Performance by location</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead className="text-right">Collection Rate</TableHead>
                  <TableHead className="text-right">Bad Debt Rate</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map(location => (
                  <TableRow key={location.id} data-testid={`row-location-${location.id}`}>
                    <TableCell className="font-medium">{location.locationName}</TableCell>
                    <TableCell>{location.region || "-"}</TableCell>
                    <TableCell>{location.city}, {location.state}</TableCell>
                    <TableCell className="text-right">{formatPercentage(location.baselineCollectionRate)}</TableCell>
                    <TableCell className="text-right">{formatPercentage(location.baselineBadDebtRate)}</TableCell>
                    <TableCell>
                      <Badge variant={location.isActive ? "default" : "secondary"}>
                        {location.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function PartnerPortalPage() {
  usePageTitle("Partner Portal", "Hospital partner portal for accessing pre-funded patient wallets and managing care orders.");
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [, params] = useRoute("/partner-portal/:id");
  
  // Use the route parameter if available, otherwise fall back to user's hospitalPartnerId
  const hospitalPartnerId = params?.id || user?.hospitalPartnerId;
  
  const { data: dashboardData, isLoading, error, refetch } = useQuery<PartnerDashboardData>({
    queryKey: ["/api/partner-portal", hospitalPartnerId, "dashboard"],
    enabled: !!hospitalPartnerId,
  });
  
  if (!hospitalPartnerId) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Building2 className="h-16 w-16 text-muted-foreground" />
        <h2 className="text-xl font-semibold">{t("partnerPortal.notLinked", "Not Linked to Hospital Partner")}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t("partnerPortal.notLinkedDesc", "Your account is not linked to a hospital partner. Please contact an administrator to set up your access.")}
        </p>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-muted-foreground">{t("common.loading", "Loading...")}</div>
      </div>
    );
  }
  
  if (error || !dashboardData) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <AlertCircle className="h-16 w-16 text-destructive" />
        <h2 className="text-xl font-semibold">{t("common.error", "Error")}</h2>
        <p className="text-muted-foreground">{t("partnerPortal.loadError", "Failed to load dashboard data")}</p>
        <Button onClick={() => refetch()}>{t("common.retry", "Retry")}</Button>
      </div>
    );
  }
  
  const { partner, paymentRequests, stats } = dashboardData;
  
  return (
    <div className="space-y-6">
      <PageAbout
        title="Partner Portal"
        description="Access your hospital's HealthWallet dashboard to manage orders, request payments from patient wallets, and track revenue cycle performance with ROI metrics."
        features={[
          "View orders and deliveries for your healthcare provider",
          "Request payments from pre-funded patient wallets",
          "Track payment status and approval workflow",
          "View Revenue Cycle & ROI performance metrics",
          "Access multi-location national roll-up data"
        ]}
        tips={[
          "Check the Revenue Cycle tab for financial impact metrics",
          "Payment requests require wallet approval before processing",
          "Use the ROI report to demonstrate platform value"
        ]}
      />
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="text-page-title">
            <Building2 className="h-8 w-8" />
            {partner.partnerName}
          </h1>
          <div className="text-muted-foreground flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {partner.partnerCode}
            <Badge variant={partner.status === "active" ? "default" : "secondary"}>
              {partner.status}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-full px-3 py-1.5 bg-muted/30" data-testid="badge-powered-by">
            <span>Powered by</span>
            <img 
              src={healthWalletLogo} 
              alt="HealthWallet" 
              className="h-5 w-5 object-contain"
              width="1024"
              height="1024"
              loading="lazy"
              decoding="async"
            />
            <span className="font-medium text-foreground">HealthWallet</span>
          </div>
          <PaymentRequestDialog 
            partnerId={hospitalPartnerId} 
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/partner-portal", hospitalPartnerId, "dashboard"] });
            }}
          />
        </div>
      </div>
      
      <Tabs defaultValue="payments" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="payments" data-testid="tab-payments">
            <CreditCard className="h-4 w-4 mr-2" />
            {t("partnerPortal.paymentRequests", "Payment Requests")}
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">
            <MapPin className="h-4 w-4 mr-2" />
            {t("partnerPortal.locations", "Locations")}
          </TabsTrigger>
          <TabsTrigger value="revenue-cycle" data-testid="tab-revenue-cycle">
            <BarChart3 className="h-4 w-4 mr-2" />
            {t("partnerPortal.revenueCycle", "Revenue Cycle & ROI")}
          </TabsTrigger>
          <TabsTrigger value="emergency-care" data-testid="tab-emergency-care">
            <Heart className="h-4 w-4 mr-2" />
            {t("partnerPortal.emergencyCare", "Emergency Care")}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Building2 className="h-4 w-4 mr-2" />
            {t("partnerPortal.partnerInfo", "Partner Info")}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="locations" className="space-y-4">
          <LocationsTab partnerId={hospitalPartnerId} />
        </TabsContent>
        
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("partnerPortal.paymentRequests", "Payment Requests")}</CardTitle>
              <CardDescription>
                {t("partnerPortal.paymentRequestsDesc", "Requests for payment from patient wallets")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {paymentRequests.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t("partnerPortal.noPaymentRequests", "No payment requests found")}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("partnerPortal.service", "Service")}</TableHead>
                      <TableHead>{t("partnerPortal.amount", "Amount")}</TableHead>
                      <TableHead>{t("partnerPortal.status", "Status")}</TableHead>
                      <TableHead>{t("partnerPortal.invoice", "Invoice")}</TableHead>
                      <TableHead>{t("partnerPortal.requested", "Requested")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentRequests.map(payment => (
                      <TableRow key={payment.id} data-testid={`row-payment-${payment.id}`}>
                        <TableCell className="max-w-[200px] truncate">
                          {payment.serviceDescription || "-"}
                        </TableCell>
                        <TableCell className="font-medium">
                          ${(payment.amount / 100).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={payment.status} />
                        </TableCell>
                        <TableCell className="font-mono">
                          {payment.invoiceNumber || "-"}
                        </TableCell>
                        <TableCell>
                          {payment.requestedAt ? format(new Date(payment.requestedAt), "MMM d, yyyy") : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="emergency-care" className="space-y-4">
          <EmergencyCareTab partnerId={hospitalPartnerId} />
        </TabsContent>

        <TabsContent value="revenue-cycle" className="space-y-4">
          <RevenueCycleTab partnerId={hospitalPartnerId} />
        </TabsContent>
        
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("partnerPortal.partnerInfo", "Partner Information")}</CardTitle>
              <CardDescription>
                {t("partnerPortal.partnerInfoDesc", "Your hospital partnership details")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t("partnerPortal.partnerName", "Partner Name")}</Label>
                  <p className="font-medium">{partner.partnerName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("partnerPortal.partnerCode", "Partner Code")}</Label>
                  <p className="font-mono">{partner.partnerCode}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("partnerPortal.contactName", "Contact Name")}</Label>
                  <p>{partner.contactName || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("partnerPortal.contactEmail", "Contact Email")}</Label>
                  <p>{partner.contactEmail || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("partnerPortal.contactPhone", "Contact Phone")}</Label>
                  <p>{partner.contactPhone || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("partnerPortal.status", "Status")}</Label>
                  <Badge variant={partner.status === "active" ? "default" : "secondary"}>
                    {partner.status}
                  </Badge>
                </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4">{t("partnerPortal.contractTerms", "Contract Terms")}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">{t("partnerPortal.paymentTerms", "Payment Terms")}</Label>
                    <p>NET {partner.paymentTermsDays} days</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t("partnerPortal.revenueShare", "Revenue Share")}</Label>
                    <p>{partner.revenueSharePercent}%</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t("partnerPortal.contractStart", "Contract Start")}</Label>
                    <p>{partner.contractStartDate ? format(new Date(partner.contractStartDate), "MMM d, yyyy") : "-"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">{t("partnerPortal.contractEnd", "Contract End")}</Label>
                    <p>{partner.contractEndDate ? format(new Date(partner.contractEndDate), "MMM d, yyyy") : "-"}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
